import { useState } from 'preact/hooks';
import { CardTile, Empty, ErrorBox, Loading, minutes } from '../components/ui';
import { api, qs, type CardSummary, type Page } from '../lib/api';
import { Link, navigate } from '../lib/router';
import { errorText, toast, useAsync, useIsAdmin } from '../lib/store';

/** Darkroom's curiosity button: open a random recipe. */
export async function shuffle() {
  try {
    const r = await api.get<Page<CardSummary>>(`/cards${qs({ type: 'recipe', sort: 'random', limit: 1 })}`);
    if (r.items[0]) navigate(`/c/${r.items[0].id}/${r.items[0].slug}`);
  } catch (e) {
    toast(errorText(e), 'error');
  }
}

/** A random active recipe with a photo, other than `not` when there is a choice. */
async function pickFocus(not?: string): Promise<{ card: CardSummary | null; total: number }> {
  const r = await api.get<Page<CardSummary>>(`/cards${qs({ type: 'recipe', status: 'active', has_cover: 1, sort: 'random', limit: 2 })}`);
  return { card: r.items.find((c) => c.id !== not) ?? r.items[0] ?? null, total: r.total };
}

/** Cards created or changed by ChatGPT/Claude that the owner hasn't checked yet. */
const reviewQueue = () => api.get<Page<CardSummary>>(`/cards${qs({ needs_review: 1, status: 'all', limit: 100 })}`);

/** The overview: what Bakery is, one recipe in focus (a new one each visit), and the way into the rest. */
export function Home() {
  const admin = useIsAdmin();
  const [focus, setFocus] = useState<CardSummary | null>(null);
  const { data, error, loading, setData } = useAsync(async () => {
    const [first, review] = await Promise.all([
      pickFocus(),
      admin ? reviewQueue() : Promise.resolve(null),
    ]);
    return { first, review };
  }, [admin]);

  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const d = data!;
  const c = focus ?? d.first.card;

  const another = async () => {
    try {
      setFocus((await pickFocus(c?.id)).card);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const approveAll = async () => {
    const items = d.review?.items ?? [];
    if (!items.length || !confirm(`Mark ${items.length === 1 ? 'this card' : `all ${items.length} cards`} as reviewed?`)) return;
    try {
      const { reviewed } = await api.post<{ reviewed: number }>('/cards/reviewed', { ids: items.map((r) => r.id) });
      toast(`Marked ${reviewed} ${reviewed === 1 ? 'card' : 'cards'} reviewed`);
      setData({ ...d, review: await reviewQueue() });
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  return (
    <>
      <header class="intro">
        <p class="dr-label">Bread &amp; baking</p>
        <h1 class="dr-section-title">
          A home for bread<span class="dr-stop">.</span>
        </h1>
        <div class="intro-text">
          <p>
            Bakery collects bread recipes: everyday loaves, sourdough, flatbreads and enriched doughs, each with notes from the times it has been
            baked.
          </p>
          <p class="muted">
            Every recipe scales to the batch you want, switches between metric and US units, and opens in Bake mode: one step at a time, with
            timers and the screen kept awake.
          </p>
        </div>
      </header>

      {d.review && d.review.total > 0 && (
        <section class="section" aria-labelledby="review-h">
          <div class="row-between">
            <h2 id="review-h">
              Needs review <span class="dr-tag dr-tag--alert">{d.review.total}</span>
            </h2>
            <div class="row">
              <span class="muted small">Created or changed by ChatGPT — check, then mark reviewed.</span>
              <button type="button" class="dr-btn bk-sm" onClick={() => void approveAll()}>
                Mark all reviewed
              </button>
            </div>
          </div>
          <div class="strip">
            {d.review.items.map((r) => (
              <CardTile key={r.id} c={r} />
            ))}
          </div>
        </section>
      )}

      {c ? (
        <section class="section" aria-labelledby="focus-h">
          <div class="row-between focus-head">
            <p class="dr-label">In focus</p>
            {d.first.total > 1 && (
              <button type="button" class="dr-btn dr-btn--shuffle" onClick={() => void another()}>
                Show me another
              </button>
            )}
          </div>
          <Link class="focus" href={`/c/${c.id}/${c.slug}`}>
            <div class="detail-cover dr-photo">
              {c.cover ? <img src={c.cover.url} alt={c.cover.alt_text ?? c.title} /> : <span class="dr-plate">Recipe</span>}
            </div>
            <div class="stack">
              <h2 id="focus-h" class="focus-title">
                {c.title}
              </h2>
              {c.summary && <p class="muted">{c.summary}</p>}
              {(c.face.total_min || c.face.servings) && (
                <dl class="dr-specs">
                  {c.face.total_min ? (
                    <div>
                      <dt>Total</dt>
                      <dd>{minutes(c.face.total_min)}</dd>
                    </div>
                  ) : null}
                  {c.face.servings ? (
                    <div>
                      <dt>Makes</dt>
                      <dd>{c.face.servings}</dd>
                    </div>
                  ) : null}
                </dl>
              )}
              {c.tags.length > 0 && (
                <div class="row">
                  {c.tags.slice(0, 6).map((t) => (
                    <span key={t} class="dr-tag">
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <span class="focus-cta">Read the recipe →</span>
            </div>
          </Link>
        </section>
      ) : (
        admin && (
          <Empty>
            <p>Only recipes with a photo are featured here. Add a cover photo to a recipe to see it in focus.</p>
            <Link class="dr-btn dr-btn--primary" href="/capture">
              Add a recipe
            </Link>
          </Empty>
        )
      )}


      <Link class="dr-next" href="/browse">
        <span class="dr-label">Next frame</span>
        <span class="dr-section-title">
          Browse every recipe <span class="dr-stop">→</span>
        </span>
      </Link>
    </>
  );
}
